import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import {
  OutcomeReason,
  SubmissionStatus,
  isTerminalStatus,
  outcomeReasonRequired,
  todayIso,
  type OutcomeReason as OutcomeReasonCode,
  type SubmissionStatus as SubmissionStatusCode,
} from '@clawhire/shared';
import { api, isApiError } from '@/lib/api';
import { EMPTY, enumOptions, formatDate, formatPct, formatRate, formatRelativeDays } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SimpleSelect } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from '@/components/ui/toaster';
import { SimpleTooltip } from '@/components/ui/tooltip';
import { EnumMenuBadge } from './EnumMenuBadge';
import { useInvalidateCandidates } from './api';
import type { CandidateSubmission, StatusChangeResponse } from './types';

export interface SubmissionsTableProps {
  submissions: CandidateSubmission[];
  canEdit: boolean;
}

interface PendingChange {
  submission: CandidateSubmission;
  status: SubmissionStatusCode;
}

/**
 * The candidate's submissions with an inline status change (`POST /submissions/:id/status`);
 * Rejected / Withdrawn ask for the outcome reason first (spec §6.2).
 */
export function SubmissionsTable({ submissions, canEdit }: SubmissionsTableProps) {
  const navigate = useNavigate();
  const invalidate = useInvalidateCandidates();
  const [pending, setPending] = React.useState<PendingChange | null>(null);
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const today = todayIso();

  const setStatus = useMutation({
    mutationFn: (input: {
      id: string;
      status: SubmissionStatusCode;
      outcomeReason?: OutcomeReasonCode;
      note?: string;
    }) =>
      api.post<StatusChangeResponse>(`/submissions/${input.id}/status`, {
        status: input.status,
        outcomeReason: input.outcomeReason,
        note: input.note || undefined,
      }),
    onMutate: (input) => setBusyId(input.id),
    onSuccess: async (res, input) => {
      toast.success(`Status → ${SubmissionStatus.label(input.status)}`);
      for (const h of res.hints ?? []) toast.info(h);
      for (const w of res.warnings ?? []) toast.warning(w.message);
      setPending(null);
      await invalidate();
    },
    onError: (e: Error, input) => {
      if (isApiError(e) && e.code === 'OUTCOME_REASON_REQUIRED') {
        const submission = submissions.find((s) => s.id === input.id);
        if (submission) setPending({ submission, status: input.status });
        return;
      }
      toast.error(e.message);
    },
    onSettled: () => setBusyId(null),
  });

  const change = (submission: CandidateSubmission, status: SubmissionStatusCode) => {
    if (status === submission.status) return;
    if (outcomeReasonRequired(status) && status !== 'PLACED') {
      setPending({ submission, status });
      return;
    }
    setStatus.mutate({ id: submission.id, status });
  };

  if (submissions.length === 0) {
    return <p className="text-sm text-muted-foreground">Not proposed for any position yet.</p>;
  }

  return (
    <>
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <caption className="sr-only">Submissions of this candidate</caption>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Position</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Offer</TableHead>
              <TableHead className="text-right">Actual margin</TableHead>
              <TableHead>Follow-up</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {submissions.map((s) => {
              const warnings = s.rates?.warnings ?? [];
              const followUp = s.followUpOn ?? s.feedbackDue;
              const overdue = !s.computed.isTerminal && followUp !== null && followUp < today;
              return (
                <TableRow
                  key={s.id}
                  tabIndex={0}
                  className="cursor-pointer focus-visible:bg-muted/60 focus-visible:outline-none"
                  onClick={() => navigate(`/submissions/${s.id}`)}
                  onKeyDown={(e) => {
                    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      navigate(`/submissions/${s.id}`);
                    }
                  }}
                >
                  <TableCell>
                    <Link
                      to={`/positions/${s.position.id}`}
                      className="font-medium hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="tabular-nums">{s.position.requestNo}</span> {s.position.role}
                    </Link>
                    {s.nextStep ? (
                      <div className="truncate text-xs text-muted-foreground" title={s.nextStep}>
                        Next: {s.nextStep}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1.5">
                      <EnumMenuBadge
                        def={SubmissionStatus}
                        value={s.status}
                        onChange={(v) => change(s, v)}
                        readOnly={!canEdit}
                        busy={busyId === s.id}
                        label="Submission status"
                      />
                      {s.outcomeReason ? (
                        <span className="text-xs text-muted-foreground">
                          {OutcomeReason.label(s.outcomeReason)}
                        </span>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <span className="inline-flex items-center justify-end gap-1">
                      {warnings.length > 0 ? (
                        <SimpleTooltip label={warnings.map((w) => w.message).join(' ')}>
                          <TriangleAlert className="h-4 w-4 text-warning" aria-label="Rate warning" />
                        </SimpleTooltip>
                      ) : null}
                      {formatRate(s.rates?.offerInUse ?? s.clientRateEur)}
                    </span>
                    {s.candidateRateEur !== null ? (
                      <div className="text-xs text-muted-foreground">
                        cand. {formatRate(s.candidateRateEur, s.candidateRateUnit)}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatPct(s.computed.actualMarginPct)}
                  </TableCell>
                  <TableCell>
                    {followUp ? (
                      <span
                        className={cn(
                          'whitespace-nowrap',
                          (overdue || s.computed.needsChase) && 'font-medium text-destructive',
                        )}
                      >
                        <time dateTime={followUp}>{formatDate(followUp)}</time>
                        <span className="ml-1 text-xs font-normal text-muted-foreground">
                          {s.followUpOn ? formatRelativeDays(followUp, today) : 'feedback due'}
                        </span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">{EMPTY}</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <OutcomeReasonDialog
        pending={pending}
        busy={setStatus.isPending}
        onCancel={() => setPending(null)}
        onConfirm={(reason, note) => {
          if (!pending) return;
          setStatus.mutate({
            id: pending.submission.id,
            status: pending.status,
            outcomeReason: reason,
            note,
          });
        }}
      />
    </>
  );
}

const REASON_OPTIONS = enumOptions(
  OutcomeReason,
  OutcomeReason.codes.filter((c) => c !== 'PLACED'),
);

function OutcomeReasonDialog({
  pending,
  busy,
  onCancel,
  onConfirm,
}: {
  pending: PendingChange | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (reason: OutcomeReasonCode, note: string) => void;
}) {
  const [reason, setReason] = React.useState<OutcomeReasonCode | null>(null);
  const [note, setNote] = React.useState('');
  const reasonId = React.useId();
  const noteId = React.useId();
  const open = pending !== null;

  React.useEffect(() => {
    if (open) {
      setReason(pending?.status === 'WITHDRAWN' ? 'CANDIDATE_WITHDREW' : null);
      setNote('');
    }
  }, [open, pending?.status]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onCancel()}>
      <DialogContent>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (reason) onConfirm(reason, note.trim());
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {pending
                ? `Set ${pending.submission.position.requestNo} to ${SubmissionStatus.label(pending.status)}`
                : 'Outcome'}
            </DialogTitle>
            <DialogDescription>
              {pending && isTerminalStatus(pending.status)
                ? 'A closed submission needs an outcome reason; waiting-on, next step and follow-up are cleared.'
                : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor={reasonId}>Outcome reason</Label>
            <SimpleSelect
              id={reasonId}
              value={reason}
              onChange={(v) => setReason((v as OutcomeReasonCode | null) ?? null)}
              options={REASON_OPTIONS}
              placeholder="Choose a reason"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={noteId}>Note (optional)</Label>
            <Textarea
              id={noteId}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={1000}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={!reason} loading={busy}>
              Confirm
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
